/**
 * Pure TEA update for the change-history side panel.
 *
 * Shell: `ChangeHistoryPanel.vue` (`useTea` + history/restore Cmds).
 */
import { assertNever } from "../lib/assertNever";
import type { FileChangePayload, FileHistoryEntry } from "../lib/fileHistory";
import {
  atIndexMaybe,
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
} from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";
import type { HistorySelection } from "./ChangeHistoryPanel.selection";

export const HISTORY_PAGE_SIZE = 60;

export type HistoryModel = {
  readonly loadingList: boolean;
  readonly loadingMore: boolean;
  readonly loadingChange: boolean;
  readonly restoring: boolean;
  readonly error: Maybe<string>;
  readonly entries: FileHistoryEntry[];
  readonly hasMore: boolean;
  readonly selectedOid: Maybe<string>;
  readonly selectedChange: Maybe<FileChangePayload>;
  readonly selectedWhen: Maybe<string>;
};

export const initialHistoryModel: HistoryModel = {
  loadingList: false,
  loadingMore: false,
  loadingChange: false,
  restoring: false,
  error: nothing,
  entries: [],
  hasMore: false,
  selectedOid: nothing,
  selectedChange: nothing,
  selectedWhen: nothing,
};

export type HistoryMsg =
  | { readonly type: "ClosedOrMissingPath" }
  | {
      readonly type: "OpenPaths";
      readonly root: string;
      readonly relativePath: string;
    }
  | {
      readonly type: "ListLoaded";
      readonly entries: FileHistoryEntry[];
    }
  | { readonly type: "ListFailed"; readonly error: string }
  | { readonly type: "SelectOid"; readonly oid: string }
  | {
      readonly type: "ChangeLoaded";
      readonly change: FileChangePayload;
      readonly when: string;
      readonly selection: HistorySelection;
    }
  | { readonly type: "ChangeFailed"; readonly error: string }
  | { readonly type: "LoadMore" }
  | {
      readonly type: "MoreLoaded";
      readonly entries: FileHistoryEntry[];
      readonly requestedLimit: number;
    }
  | { readonly type: "MoreFailed"; readonly error: string }
  | { readonly type: "RestoreRequested" }
  | { readonly type: "RestoreConfirmed"; readonly content: string }
  | { readonly type: "RestoreCancelled" }
  | { readonly type: "RestoreFinished" };

export type HistoryCmd =
  | {
      readonly kind: "loadList";
      readonly root: string;
      readonly relativePath: string;
    }
  | {
      readonly kind: "loadChange";
      readonly root: string;
      readonly relativePath: string;
      readonly oid: string;
    }
  | {
      readonly kind: "loadMore";
      readonly root: string;
      readonly relativePath: string;
      readonly nextLimit: number;
    }
  | {
      readonly kind: "confirmRestore";
      readonly content: string;
      readonly when: string;
    }
  | { readonly kind: "emitSelect"; readonly selection: Maybe<HistorySelection> }
  | { readonly kind: "emitRestore"; readonly content: string };

export type HistoryPathContext = {
  readonly root: Maybe<string>;
  readonly relativePath: Maybe<string>;
};

function clearSelectionCmds(): readonly HistoryCmd[] {
  return [{ kind: "emitSelect", selection: nothing }];
}

export function updateHistory(
  model: HistoryModel,
  msg: HistoryMsg,
  paths: HistoryPathContext,
): readonly [HistoryModel, readonly HistoryCmd[]] {
  switch (msg.type) {
    case "ClosedOrMissingPath": {
      const hadSelection =
        isJust(model.selectedOid) || isJust(model.selectedChange);
      return [initialHistoryModel, hadSelection ? clearSelectionCmds() : []];
    }
    case "OpenPaths":
      return [
        {
          ...initialHistoryModel,
          loadingList: true,
        },
        [
          {
            kind: "loadList",
            root: msg.root,
            relativePath: msg.relativePath,
          },
        ],
      ];
    case "ListLoaded": {
      const first = atIndexMaybe(msg.entries, 0);
      const selectedOid = isJust(first) ? just(first.value.oid) : nothing;
      const cmds: HistoryCmd[] = [];
      if (isNothing(selectedOid)) {
        cmds.push({ kind: "emitSelect", selection: nothing });
      } else if (isJust(paths.root) && isJust(paths.relativePath)) {
        cmds.push({
          kind: "loadChange",
          root: paths.root.value,
          relativePath: paths.relativePath.value,
          oid: selectedOid.value,
        });
      }
      return [
        {
          ...model,
          loadingList: false,
          loadingMore: false,
          loadingChange: isJust(selectedOid),
          error: nothing,
          entries: msg.entries,
          hasMore: msg.entries.length === HISTORY_PAGE_SIZE,
          selectedOid,
          selectedChange: nothing,
          selectedWhen: nothing,
        },
        cmds,
      ];
    }
    case "ListFailed":
      return [
        {
          ...initialHistoryModel,
          error: just(msg.error),
        },
        clearSelectionCmds(),
      ];
    case "SelectOid": {
      if (isJust(model.selectedOid) && model.selectedOid.value === msg.oid) {
        return [model, []];
      }
      if (isNothing(paths.root) || isNothing(paths.relativePath)) {
        return [
          {
            ...model,
            selectedOid: just(msg.oid),
            selectedChange: nothing,
            selectedWhen: nothing,
            loadingChange: false,
          },
          clearSelectionCmds(),
        ];
      }
      return [
        {
          ...model,
          selectedOid: just(msg.oid),
          selectedChange: nothing,
          selectedWhen: nothing,
          loadingChange: true,
          error: nothing,
        },
        [
          {
            kind: "loadChange",
            root: paths.root.value,
            relativePath: paths.relativePath.value,
            oid: msg.oid,
          },
        ],
      ];
    }
    case "ChangeLoaded":
      return [
        {
          ...model,
          loadingChange: false,
          error: nothing,
          selectedChange: just(msg.change),
          selectedWhen: just(msg.when),
        },
        [{ kind: "emitSelect", selection: just(msg.selection) }],
      ];
    case "ChangeFailed":
      return [
        {
          ...model,
          loadingChange: false,
          error: just(msg.error),
          selectedChange: nothing,
          selectedWhen: nothing,
        },
        clearSelectionCmds(),
      ];
    case "LoadMore": {
      if (
        model.loadingList === true ||
        model.loadingMore === true ||
        model.hasMore === false ||
        isNothing(paths.root) ||
        isNothing(paths.relativePath)
      ) {
        return [model, []];
      }
      const nextLimit = model.entries.length + HISTORY_PAGE_SIZE;
      return [
        { ...model, loadingMore: true, error: nothing },
        [
          {
            kind: "loadMore",
            root: paths.root.value,
            relativePath: paths.relativePath.value,
            nextLimit,
          },
        ],
      ];
    }
    case "MoreLoaded":
      return [
        {
          ...model,
          loadingMore: false,
          entries: msg.entries,
          hasMore: msg.entries.length === msg.requestedLimit,
        },
        [],
      ];
    case "MoreFailed":
      return [
        {
          ...model,
          loadingMore: false,
          error: just(msg.error),
        },
        [],
      ];
    case "RestoreRequested": {
      if (
        isNothing(model.selectedChange) ||
        model.loadingChange === true ||
        model.restoring === true
      ) {
        return [model, []];
      }
      const when = isJust(model.selectedWhen)
        ? model.selectedWhen.value
        : "this point";
      return [
        model,
        [
          {
            kind: "confirmRestore",
            content: model.selectedChange.value.after,
            when,
          },
        ],
      ];
    }
    case "RestoreConfirmed":
      return [
        { ...model, restoring: true },
        [{ kind: "emitRestore", content: msg.content }],
      ];
    case "RestoreCancelled":
      return [model, []];
    case "RestoreFinished":
      return [{ ...model, restoring: false }, []];
    default:
      return assertNever(msg);
  }
}

export function makeHistoryUpdate(
  getPaths: () => HistoryPathContext,
): UpdateFn<HistoryModel, HistoryMsg, HistoryCmd> {
  return (model, msg) => updateHistory(model, msg, getPaths());
}
